import { useEffect, useRef, useState } from "react";
import { categories } from "@/mocks/content";

const NAV_LINKS = [
  { href: "#feature", label: "深读头条" },
  { href: "#columns", label: "深度栏目" },
  { href: "#archive", label: "真相档案" },
  { href: "#clues", label: "独家线索" },
  { href: "#analysis", label: "深层解析" },
  { href: "#map", label: "关系图谱" },
  { href: "#about", label: "关于我们" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [keyword, setKeyword] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (showSearch) searchRef.current?.focus();
  }, [showSearch]);

  const resultTitles = articlesMatch(keyword);

  const base = scrolled
    ? "bg-background-50/95 border-background-300/70 backdrop-blur text-foreground-950"
    : "bg-transparent text-background-100";

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className={`transition-colors duration-300 border-b ${base} ${scrolled ? "shadow-sm border" : "border-white/10"}`}>
        <nav className="px-4 md:px-6" aria-label="主导航">
          <div className="mx-auto flex h-16 max-w-[1280px] items-center justify-between gap-4">
            <a href="#hero" className="flex items-center gap-2.5 whitespace-nowrap">
              <span className="grid h-9 w-9 place-items-center rounded-md bg-primary-500 font-heading text-lg font-black text-background-50">
                料
              </span>
              <span className="flex flex-col leading-none">
                <span className="font-heading text-xl font-black tracking-tight">看料网</span>
                <span className="mt-0.5 text-[9px] uppercase tracking-[0.3em] text-current/60">KanLiao</span>
              </span>
            </a>

            <ul className="hidden items-center gap-1 lg:flex">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-background-200/70 hover:text-primary-500"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>

            <div className="hidden items-center gap-2 lg:flex">
              <button
                type="button"
                onClick={() => setShowSearch(true)}
                aria-label="站内搜索"
                className="grid h-9 w-9 place-items-center rounded-md border border-current/20 transition-colors hover:border-primary-400 hover:text-primary-500"
              >
                <i className="ri-search-line text-lg" />
              </button>
              <a
                href="#subscribe"
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                线索投稿
              </a>
            </div>

            <button
              type="button"
              aria-label="展开菜单"
              onClick={() => setOpen((v) => !v)}
              className="grid h-10 w-10 place-items-center rounded-md border border-current/20 lg:hidden"
            >
              <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-xl`} />
            </button>
          </div>
        </nav>

        {open && (
          <div className="border-t border-current/10 bg-background-50 text-foreground-950 lg:hidden">
            <ul className="mx-auto max-w-[1280px] px-4 py-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between rounded-md px-3 py-3 text-base font-medium hover:bg-background-100"
                  >
                    {l.label}
                    <i className="ri-arrow-right-line text-background-400" />
                  </a>
                </li>
              ))}
            </ul>
            <div className="mx-auto max-w-[1280px] px-4 pb-4">
              <a
                href="#subscribe"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50"
              >
                线索投稿 / 订阅
              </a>
            </div>
          </div>
        )}
      </div>

      {showSearch && (
        <div className="mx-auto mt-2 max-w-[1280px] px-4">
          <div className="rounded-xl border border-background-300 bg-background-50 p-3 shadow-sm">
            <div className="flex items-center gap-2 rounded-lg bg-background-100 px-3">
              <i className="ri-search-line text-background-500" />
              <input
                ref={searchRef}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    document.getElementById("columns")?.scrollIntoView({ behavior: "smooth" });
                    setShowSearch(false);
                    setKeyword("");
                  }
                }}
                placeholder="搜索标题，例如：真相 / 线索 / 图谱"
                className="w-full bg-transparent py-3 text-sm outline-none"
              />
              <button
                type="button"
                onClick={() => setShowSearch(false)}
                aria-label="关闭搜索"
                className="grid h-8 w-8 place-items-center rounded-md text-background-500 hover:bg-background-200"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            {keyword.trim() !== "" && (
              <ul className="mt-2 divide-y divide-background-200">
                {resultTitles.length === 0 && (
                  <li className="px-2 py-3 text-sm text-background-600">没有匹配的选题，换个关键词试试。</li>
                )}
                {resultTitles.map((t, i) => (
                  <li key={t}>
                    <a
                      href="#columns"
                      onClick={() => {
                        setShowSearch(false);
                        setKeyword("");
                      }}
                      className="flex items-center gap-3 rounded-md px-2 py-2.5 text-sm transition-colors hover:bg-background-100"
                    >
                      <span className="font-heading text-background-400">{String(i + 1).padStart(2, "0")}</span>
                      {t}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function articlesMatch(keyword: string) {
  const k = keyword.trim().toLowerCase();
  const pool = [...categories.map((c) => c.name), "头条深读", "真相档案", "独家线索", "深层解析", "关系图谱"];
  return pool.filter((x) => x.toLowerCase().includes(k)).slice(0, 6);
}